import { merge, cloneDeep } from 'lodash';
import { PendingEntitiesInstance } from 'types';
import { falsyCheckDate } from 'utils';
import EntityIdOptionSelector from 'shared/EntityIdOptionSelector';
import { EllipsisDiv } from 'styles';
import { FetchPendingEntityStatusPayload } from 'IntegrationModule/types';

export enum StateIds {
  PendingEntityTable = 'pending-entity-table',
}

enum HeadCellIds {
  ID = 'id',
  DataType = 'dataType',
  MappingStatus = 'mappingStatus',
  MosaicTeamId = 'mosaicTeamId',
  MosaicId = 'mosaicId',
  TargetService = 'targetService',
  TargetId = 'targetId',
  IsArchived = 'isArchived',
  ArchivedAt = 'archivedAt',
  MosaicData = 'mosaicData',
  TargetData = 'targetData',
  UUID = 'uuid',
  CreatedAt = 'createdAt',
  UpdatedAt = 'UpdatedAt',
  IntegrationId = 'integrationId',
  TargetServiceId = 'targetServiceId',
  Action = '_action',
  Requeue = '_requeue',
  MosaicDataType = 'mosaicDataType',
  Settings = 'settings',
  RetryCount = 'retryCount',
  MosaicDataHash = 'mosaicDataHash',
  TargetDataHash = 'targetDataHash',
  CreateSource = 'createSource',
  UpdateSource = 'updateSource',
  WaitingResponse = 'waitingResponse',
}

type CustomRowProps = {
  onSingleEntityModalOpen: ({
    entityId,
    dataType,
  }: {
    entityId: number;
    dataType?: string;
  }) => void;
  onPendingEntityStatusModalOpen: (payload: FetchPendingEntityStatusPayload) => void;
};

type FormattedRowData = PendingEntitiesInstance & CustomRowProps;

const cellRenderer = {
  [HeadCellIds.ID]: ({
    id,
    dataType,
    onSingleEntityModalOpen,
    onPendingEntityStatusModalOpen,
    targetServiceId,
  }: FormattedRowData) => {
    return (
      <div>
        <EntityIdOptionSelector
          entityId={id}
          targetServiceId={targetServiceId}
          dataType={dataType}
          className="transparent"
          onSingleEntityModalOpen={() => onSingleEntityModalOpen({ entityId: id, dataType })}
          openPendingEntityStatusModal={onPendingEntityStatusModalOpen}
        />
      </div>
    );
  },
  [HeadCellIds.DataType]: ({ dataType }: FormattedRowData) => {
    return <EllipsisDiv>{dataType}</EllipsisDiv>;
  },
  [HeadCellIds.MappingStatus]: ({ mappingStatus }: FormattedRowData) => {
    return <EllipsisDiv>{mappingStatus}</EllipsisDiv>;
  },
  [HeadCellIds.MosaicTeamId]: ({ mosaicTeamId }: FormattedRowData) => {
    return <EllipsisDiv>{mosaicTeamId}</EllipsisDiv>;
  },
  [HeadCellIds.MosaicId]: ({ mosaicId }: FormattedRowData) => {
    return <EllipsisDiv>{mosaicId}</EllipsisDiv>;
  },
  [HeadCellIds.TargetService]: ({ targetService }: FormattedRowData) => {
    return <EllipsisDiv>{targetService}</EllipsisDiv>;
  },
  [HeadCellIds.TargetId]: ({ targetId }: FormattedRowData) => {
    return <EllipsisDiv>{targetId ?? 'N/A'}</EllipsisDiv>;
  },
  [HeadCellIds.IsArchived]: ({ isArchived }: FormattedRowData) => {
    return <EllipsisDiv>{isArchived ? 'Yes' : 'No'}</EllipsisDiv>;
  },
  [HeadCellIds.ArchivedAt]: ({ archivedAt }: FormattedRowData) => {
    return <EllipsisDiv>{falsyCheckDate(archivedAt)}</EllipsisDiv>;
  },
  //   [HeadCellIds.MosaicData]: ({ mosaicData }: FormattedRowData) => {
  //     return <div>{mosaicData}</div>;
  //   },
  //   [HeadCellIds.TargetData]: ({ targetData }: FormattedRowData) => {
  //     return (
  //       <div>
  //         <ReactJson name="details" src={{ targetData }} />
  //       </div>
  //     );
  //   },
  [HeadCellIds.UUID]: ({ uuid }: FormattedRowData) => {
    return <EllipsisDiv>{uuid}</EllipsisDiv>;
  },
  [HeadCellIds.CreatedAt]: ({ createdAt }: FormattedRowData) => {
    return <EllipsisDiv>{falsyCheckDate(createdAt)}</EllipsisDiv>;
  },
  [HeadCellIds.UpdatedAt]: ({ UpdatedAt }: FormattedRowData) => {
    return <EllipsisDiv>{falsyCheckDate(UpdatedAt)}</EllipsisDiv>;
  },
  [HeadCellIds.TargetServiceId]: ({ targetServiceId }: FormattedRowData) => {
    return <EllipsisDiv>{targetServiceId}</EllipsisDiv>;
  },
  [HeadCellIds.Action]: ({ _action }: FormattedRowData) => {
    return <EllipsisDiv>{_action}</EllipsisDiv>;
  },
  [HeadCellIds.Requeue]: ({ _requeue }: FormattedRowData) => {
    return <EllipsisDiv>{_requeue ? 'True' : 'False'}</EllipsisDiv>;
  },
  [HeadCellIds.MosaicDataType]: ({ mosaicDataType }: FormattedRowData) => {
    return <EllipsisDiv>{mosaicDataType ?? 'N/A'}</EllipsisDiv>;
  },
  //   [HeadCellIds.Settings]: ({ settings }: FormattedRowData) => {
  //     return <div>{settings}</div>;
  //   },
  [HeadCellIds.RetryCount]: ({ retryCount }: FormattedRowData) => {
    return <EllipsisDiv>{retryCount}</EllipsisDiv>;
  },
  [HeadCellIds.MosaicDataHash]: ({ mosaicDataHash }: FormattedRowData) => {
    return <div>{mosaicDataHash}</div>;
  },
  [HeadCellIds.TargetDataHash]: ({ targetDataHash }: FormattedRowData) => {
    return <div>{targetDataHash ?? 'N/A'}</div>;
  },
  [HeadCellIds.CreateSource]: ({ createSource }: FormattedRowData) => {
    return <EllipsisDiv>{createSource}</EllipsisDiv>;
  },
  [HeadCellIds.UpdateSource]: ({ updateSource }: FormattedRowData) => {
    return <div>{updateSource}</div>;
  },
  [HeadCellIds.WaitingResponse]: ({ waitingResponse }: FormattedRowData) => {
    return <div>{waitingResponse ? 'True' : 'False'}</div>;
  },
};

const tableConfigs = {
  default: {
    cellProps: {
      headCells: [
        {
          id: HeadCellIds.ID,
          label: 'Entity ID',
        },
        {
          id: HeadCellIds.DataType,
          label: 'Data Type',
        },
        {
          id: HeadCellIds.MappingStatus,
          label: 'Mapping Status',
        },
        {
          id: HeadCellIds.MosaicTeamId,
          label: 'Mosaic Team ID',
        },
        {
          id: HeadCellIds.MosaicId,
          label: 'Mosaic ID',
        },
        {
          id: HeadCellIds.TargetService,
          label: 'Target Service',
        },
        {
          id: HeadCellIds.TargetId,
          label: 'Target Id',
        },
        {
          id: HeadCellIds.IsArchived,
          label: 'Is Archived',
        },
        {
          id: HeadCellIds.ArchivedAt,
          label: 'Archived At',
        },
        {
          id: HeadCellIds.UUID,
          label: 'UUID',
        },
        {
          id: HeadCellIds.CreatedAt,
          label: 'Created At',
        },
        {
          id: HeadCellIds.UpdatedAt,
          label: 'Updated At',
        },
        {
          id: HeadCellIds.TargetServiceId,
          label: 'Target Service Id',
        },
        {
          id: HeadCellIds.Action,
          label: 'Action',
        },
        {
          id: HeadCellIds.Requeue,
          label: 'Requeue',
        },
        {
          id: HeadCellIds.MosaicDataType,
          label: 'Mosaic Data Type',
        },
        {
          id: HeadCellIds.RetryCount,
          label: 'Retry Count',
        },
        {
          id: HeadCellIds.MosaicDataHash,
          label: 'Mosaic Data Hash',
        },
        {
          id: HeadCellIds.TargetDataHash,
          label: 'Target Data Hash',
        },
        {
          id: HeadCellIds.CreateSource,
          label: 'Create Source',
        },
        {
          id: HeadCellIds.UpdateSource,
          label: 'Update Source',
        },
        {
          id: HeadCellIds.WaitingResponse,
          label: 'Waiting Response',
        },
      ],
    },
    paginationProps: {
      hidePagination: true,
    },
  },
  shared: {
    cellProps: {
      cellRenderer,
      headCells: [],
    },
    headerProps: {
      tableTitle: 'Pending Entities By Data Type and Pending Mapping Status',
    },
    paginationProps: {},
  },
};

export const getTableConfigs = ({ variant = 'default' }: { variant?: string }) => {
  const tableConfig = cloneDeep(tableConfigs[variant as keyof typeof tableConfigs]);
  const sharedConfig = cloneDeep(tableConfigs.shared);

  return merge(sharedConfig, tableConfig);
};
